import React from 'react';
import { Link } from 'react-router-dom';
import './Homepage.css';

const gameAppOrigin =
  typeof window !== 'undefined' &&
  !window.location.hostname.startsWith('game.') &&
  window.location.hostname.includes('tellcrypt.com')
    ? 'https://game.tellcrypt.com'
    : '';

const Homepage: React.FC = () => {
  const loginTo = gameAppOrigin ? `${gameAppOrigin}/login` : '/login';
  const registerTo = gameAppOrigin ? `${gameAppOrigin}/register` : '/register';

  return (
    <div className="homepage">
      {/* Hero Section */}
      <header className="hero">
        <nav className="hero-nav">
          <div className="hero-nav-brand">
            <img src="/logo.png" alt="Tellcrypt" className="hero-logo" />
            <span>Tellcrypt</span>
          </div>
          <div className="hero-nav-links">
            <a href="#games" className="hero-link">Games</a>
            <a href="#how-it-works" className="hero-link">How It Works</a>
            {gameAppOrigin ? (
              <a href={loginTo} className="hero-btn-secondary">Sign In</a>
            ) : (
              <Link to="/login" className="hero-btn-secondary">Sign In</Link>
            )}
            {gameAppOrigin ? (
              <a href={registerTo} className="hero-btn-primary">Play Free</a>
            ) : (
              <Link to="/register" className="hero-btn-primary">Play Free</Link>
            )}
          </div>
        </nav>

        <div className="hero-content">
          <div className="hero-text">
            <h1>
              Hungarian Card Games,<br />
              <span className="gradient-text">Played Online</span>
            </h1>
            <p className="hero-subtitle">
              The authentic William Tell deck brought to life. Play Pharaon, Raub, 
              and more with friends or challenge players worldwide.
            </p>
            <div className="hero-cta">
              {gameAppOrigin ? (
                <a href={registerTo} className="cta-primary">Start Playing</a>
              ) : (
                <Link to="/register" className="cta-primary">Start Playing</Link>
              )}
              <a href="#games" className="cta-secondary">Learn the Games</a>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-value">32</span>
                <span className="stat-label">Card Deck</span>
              </div>
              <div className="stat">
                <span className="stat-value">2+</span>
                <span className="stat-label">Game Variants</span>
              </div>
              <div className="stat">
                <span className="stat-value">2-6</span>
                <span className="stat-label">Players</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="card-fan">
              <div className="fan-card" style={{ '--i': 0 } as React.CSSProperties}>
                <img src="/assets/8D.png" alt="Ace of Leaf" />
              </div>
              <div className="fan-card" style={{ '--i': 1 } as React.CSSProperties}>
                <img src="/assets/7C.png" alt="King of Heart" />
              </div>
              <div className="fan-card" style={{ '--i': 2 } as React.CSSProperties}>
                <img src="/assets/6A.png" alt="Ober of Acorn" />
              </div>
              <div className="fan-card" style={{ '--i': 3 } as React.CSSProperties}>
                <img src="/assets/5D.png" alt="Unter of Leaf" />
              </div>
              <div className="fan-card" style={{ '--i': 4 } as React.CSSProperties}>
                <img src="/assets/1B.png" alt="7 of Ball" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* The Deck Section */}
      <section className="deck-section" id="deck">
        <div className="section-container">
          <h2 className="section-title">The Hungarian Deck</h2>
          <p className="section-subtitle">
            32 cards, 4 suits, centuries of tradition. The William Tell deck features 
            Acorn, Ball, Heart, and Leaf suits with unique court cards.
          </p>
          <div className="suits-grid">
            <div className="suit-card">
              <div className="suit-icon acorn">
                <img src="/assets/8A.png" alt="Acorn Ace" />
              </div>
              <h3>Acorn</h3>
              <p>Strength and endurance</p>
            </div>
            <div className="suit-card">
              <div className="suit-icon ball">
                <img src="/assets/8B.png" alt="Ball Ace" />
              </div>
              <h3>Ball</h3>
              <p>Wealth and commerce</p>
            </div>
            <div className="suit-card">
              <div className="suit-icon heart">
                <img src="/assets/8C.png" alt="Heart Ace" />
              </div>
              <h3>Heart</h3>
              <p>Love and passion</p>
            </div>
            <div className="suit-card">
              <div className="suit-icon leaf">
                <img src="/assets/8D.png" alt="Leaf Ace" />
              </div>
              <h3>Leaf</h3>
              <p>Growth and renewal</p>
            </div>
          </div>
          <div className="rank-explainer">
            <span className="rank-item">7</span>
            <span className="rank-item">8</span>
            <span className="rank-item">9</span>
            <span className="rank-item">10</span>
            <span className="rank-item highlight">Unter</span>
            <span className="rank-item highlight">Ober</span>
            <span className="rank-item highlight">King</span>
            <span className="rank-item highlight">Ace</span>
          </div>
        </div>
      </section>

      {/* Games Section */}
      <section className="games-section" id="games">
        <div className="section-container">
          <h2 className="section-title">Choose Your Game</h2>
          <p className="section-subtitle">
            Each game uses the same 32-card Hungarian deck but plays completely differently.
          </p>

          <div className="game-showcase">
            {/* Pharaon */}
            <div className="game-card-showcase">
              <div className="game-card-header pharaon-header">
                <span className="game-badge">Shedding</span>
                <h3>Pharaon</h3>
              </div>
              <div className="game-card-body">
                <p className="game-description">
                  Get rid of your cards first! Match the top card by suit or rank. 
                  Special cards change the flow -- the Green Unter stops seven-chains, 
                  Obers let you choose the suit, and Aces skip the next player.
                </p>
                <div className="game-details">
                  <div className="detail">
                    <span className="detail-icon">👥</span>
                    <span>2-6 players</span>
                  </div>
                  <div className="detail">
                    <span className="detail-icon">🎯</span>
                    <span>Score under 100</span>
                  </div>
                  <div className="detail">
                    <span className="detail-icon">⏱️</span>
                    <span>15-30 min</span>
                  </div>
                </div>
                <div className="special-cards">
                  <h4>Key Cards</h4>
                  <div className="special-cards-row">
                    <div className="special-card-info">
                      <img src="/assets/1A.png" alt="7" />
                      <span>Draw 2</span>
                    </div>
                    <div className="special-card-info">
                      <img src="/assets/5D.png" alt="Green Unter" />
                      <span>Stops 7s</span>
                    </div>
                    <div className="special-card-info">
                      <img src="/assets/6A.png" alt="Ober" />
                      <span>Pick Suit</span>
                    </div>
                    <div className="special-card-info">
                      <img src="/assets/8A.png" alt="Ace" />
                      <span>Skip</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Raub */}
            <div className="game-card-showcase">
              <div className="game-card-header raub-header">
                <span className="game-badge">Trick-Taking</span>
                <h3>Raub</h3>
              </div>
              <div className="game-card-body">
                <p className="game-description">
                  A strategic bidding and trick-taking game. Declare "Raub" to take on 
                  the table, bid to lead, or defend against the declarer. 
                  First to reach 0 points wins!
                </p>
                <div className="game-details">
                  <div className="detail">
                    <span className="detail-icon">👥</span>
                    <span>2-4 players</span>
                  </div>
                  <div className="detail">
                    <span className="detail-icon">🎯</span>
                    <span>Start at 21 → 0</span>
                  </div>
                  <div className="detail">
                    <span className="detail-icon">⏱️</span>
                    <span>20-45 min</span>
                  </div>
                </div>
                <div className="game-phases">
                  <h4>Game Flow</h4>
                  <div className="phase-flow">
                    <span className="phase-step">Deal</span>
                    <span className="phase-arrow">→</span>
                    <span className="phase-step">Bid</span>
                    <span className="phase-arrow">→</span>
                    <span className="phase-step">Defend</span>
                    <span className="phase-arrow">→</span>
                    <span className="phase-step">Play</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="more-games-teaser">
            <p>More games coming soon -- the engine supports any Hungarian card game variant!</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-section" id="how-it-works">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Create Account</h3>
              <p>Sign up in seconds. No payment needed for friendly games.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Create or Join</h3>
              <p>Host a game table or join a public one. Invite friends with a code.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Cut & Deal</h3>
              <p>The traditional cut determines trump or starter card. Cards are dealt automatically.</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <h3>Play!</h3>
              <p>Real-time multiplayer with instant updates. Works on any device.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="section-container">
          <div className="features-grid">
            <div className="feature">
              <span className="feature-icon">📱</span>
              <h3>Mobile Ready</h3>
              <p>Play anywhere -- optimized for phones and tablets with touch controls.</p>
            </div>
            <div className="feature">
              <span className="feature-icon">⚡</span>
              <h3>Real-Time</h3>
              <p>Instant card plays with WebSocket technology. No refreshing needed.</p>
            </div>
            <div className="feature">
              <span className="feature-icon">🏆</span>
              <h3>Competitive</h3>
              <p>Track your stats, win streaks, and climb the leaderboards.</p>
            </div>
            <div className="feature">
              <span className="feature-icon">🎲</span>
              <h3>Extensible</h3>
              <p>New Hungarian card games are being added. Same deck, different rules.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="cta-section">
        <div className="section-container">
          <h2>Ready to Play?</h2>
          <p>Join the table. Free to play, no downloads needed.</p>
          {gameAppOrigin ? (
            <a href={registerTo} className="cta-primary large">Create Free Account</a>
          ) : (
            <Link to="/register" className="cta-primary large">Create Free Account</Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src="/logo.png" alt="Tellcrypt" />
              <span>Tellcrypt</span>
            </div>
            <p className="footer-text">Hungarian card games, brought online.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
